<script setup lang="ts">
import type { AncestorHop } from './types';

// One chain per context the node belongs to (root-first).
defineProps<{ ancestors: AncestorHop[][] }>();
</script>

<template>
  <section class="pp-section pp-ancestors">
    <div class="pp-section-title">Ancestors</div>

    <div v-if="!ancestors.length" class="text-muted small">
      No parent context — this is a top-level page.
    </div>

    <div v-else class="pp-chains">
      <ol
        v-for="(chain, ci) in ancestors"
        :key="ci"
        class="pp-chain list-unstyled mb-0"
        :class="{ 'pp-chain-multi': ancestors.length > 1 }"
      >
        <li v-for="(hop, i) in chain" :key="hop.collection.id + '-' + i" class="pp-hop">
          <span class="pp-hop-indent" :style="{ paddingLeft: i * 1.1 + 'rem' }">
            <i
              class="bi"
              :class="hop.is_root ? 'bi-diagram-3-fill' : 'bi-chevron-right'"
              aria-hidden="true"
            ></i>
            <a class="pp-pill pp-pill-collection" :href="hop.collection.url">
              <img
                v-if="hop.collection.logo"
                :src="hop.collection.logo"
                class="pp-logo"
                alt=""
                aria-hidden="true"
              />
              {{ hop.collection.label }}
            </a>
            <a
              v-if="hop.area"
              class="pp-pill pp-pill-area"
              :href="hop.area.url"
              :title="hop.area.type_label || 'Area'"
            >{{ hop.area.label }}</a>
            <a
              v-if="hop.pathway"
              class="pp-pill pp-pill-pathway"
              :href="hop.pathway.url"
              title="Pathway"
            >{{ hop.pathway.label }}</a>
          </span>
        </li>
      </ol>
    </div>
  </section>
</template>

<style scoped>
.pp-chains {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
/* When the node has several contexts, separate each lineage visually. */
.pp-chain-multi {
  padding-left: 0.5rem;
  border-left: 3px solid #cfe2ff;
}
.pp-chain {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.pp-hop-indent {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.pp-hop-indent > .bi {
  color: #6c757d;
}
.pp-pill {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.85rem;
  text-decoration: none;
  line-height: 1.4;
}
.pp-pill-collection {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e7f1ff;
  color: #0d6efd;
  font-weight: 600;
}
.pp-logo {
  width: 1.1rem;
  height: 1.1rem;
  object-fit: contain;
  border-radius: 0.2rem;
  flex: 0 0 auto;
}
.pp-pill-area {
  background: #e9f7ef;
  color: #198754;
}
.pp-pill-pathway {
  background: #fff3cd;
  color: #997404;
}
.pp-pill:hover {
  text-decoration: underline;
}
</style>
